import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Shuffle, Loader, Search, Upload } from 'lucide-react';
import { generatePicsumImage, getRandomSeed } from '../utils/imageSearch';
import { applyImageFilter } from '../utils/imageFilters';
import { searchPixabayImages, getPixabayImageUrl, PixabayImage } from '../utils/pixabayApi';
import { TRANSLATIONS } from '../constants';

interface BackgroundControllerProps {
  currentSeed: string;
  blur: number;
  grayscale: boolean;
  width: number;
  height: number;
  backgroundType?: 'image' | 'color' | 'animated';
  previousSlideBackgroundSeed?: string; // 이전 슬라이드 배경 시드
  firstSlideBackground?: {
    type: 'image' | 'color' | 'animated';
    seed?: string;
    color?: string;
    animatedEffect?: string;
    animatedOptions?: {
      backgroundColor?: string;
      color1?: string;
      color2?: string;
      scale?: number;
      speed?: number;
    };
  }; // 첫 번째 슬라이드 배경 정보
  currentPattern?: string; // 현재 선택된 패턴
  animatedEffect?: string;
  animatedOptions?: {
    backgroundColor?: string;
    color1?: string;
    color2?: string;
    scale?: number;
    speed?: number;
  };
  onSeedChange: (seed: string) => void;
  onBlurChange: (blur: number) => void;
  onGrayscaleChange: (grayscale: boolean) => void;
  onBackgroundChange?: (background: string) => void;
  onBackgroundBlobChange?: (blob: Blob) => void;
  onPatternChange?: (pattern: string) => void;
  onBackgroundTypeChange?: (type: 'image' | 'color' | 'animated') => void;
  onAnimatedEffectChange?: (effect: string) => void;
  onAnimatedOptionsChange?: (options: {
    backgroundColor?: string;
    color1?: string;
    color2?: string;
    scale?: number;
    speed?: number;
  }) => void;
  onImageElementAdd?: (imageUrl: string) => void; // 이미지를 요소로 추가하는 콜백
  isDarkMode?: boolean;
  language?: 'ko' | 'en'; // BackgroundChanger 통합을 위한 언어 설정
  slideKeywords?: string[]; // 슬라이드의 핵심 키워드
}

export const BackgroundController: React.FC<BackgroundControllerProps> = ({
  currentSeed,
  blur,
  grayscale,
  width,
  height,
  backgroundType = 'image',
  previousSlideBackgroundSeed,
  firstSlideBackground,
  currentPattern = 'none',
  animatedEffect = 'fog',
  animatedOptions = {},
  onSeedChange,
  onBlurChange,
  onGrayscaleChange,
  onBackgroundChange,
  onBackgroundBlobChange,
  onPatternChange,
  onBackgroundTypeChange,
  onAnimatedEffectChange,
  onAnimatedOptionsChange,
  onImageElementAdd,
  isDarkMode = false,
  language = 'ko',
  slideKeywords,
}) => {
  // 파일 업로드 ref (BackgroundChanger 통합)
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Timer refs for cleanup
  const retryTimersRef = useRef<{ [key: string]: NodeJS.Timeout }>({});
  const currentRetryTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // 후보 이미지들을 고정하기 위한 상태
  const [candidateSeeds, setCandidateSeeds] = useState<string[]>([]);
  // Pixabay 이미지 후보들
  const [pixabayCandidates, setPixabayCandidates] = useState<PixabayImage[]>([]);
  // 이미지 로딩 상태 관리
  const [loadingImages, setLoadingImages] = useState<{ [key: string]: boolean }>({});
  const [currentImageLoading, setCurrentImageLoading] = useState(false);
  // 이미지 에러 상태 관리
  const [errorImages, setErrorImages] = useState<{ [key: string]: boolean }>({});
  const [currentImageError, setCurrentImageError] = useState(false);
  // 재시도 횟수 관리
  const [retryCount, setRetryCount] = useState<{ [key: string]: number }>({});
  const maxRetries = 5;
  // 필터링된 이미지 캐시
  const [filteredImages, setFilteredImages] = useState<{ [key: string]: string }>({});
  const [filterProcessing, setFilterProcessing] = useState<{ [key: string]: boolean }>({});
  
  // Pixabay 검색 상태
  const [showPixabaySearch, setShowPixabaySearch] = useState(false);
  const [pixabayQuery, setPixabayQuery] = useState('');
  const [pixabayImages, setPixabayImages] = useState<PixabayImage[]>([]);
  const [pixabayLoading, setPixabayLoading] = useState(false);
  const [pixabayError, setPixabayError] = useState<string | null>(null);
  const [pixabayCategory, setPixabayCategory] = useState('');
  const [pixabayPage, setPixabayPage] = useState(1);
  const [pixabayTotalHits, setPixabayTotalHits] = useState(0);
  
  // 이미지 선택 옵션 다이얼로그 상태
  const [showImageOptionDialog, setShowImageOptionDialog] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);

  // slideKeywords가 있을 때 자동으로 Pixabay 이미지 검색
  useEffect(() => {
    if (slideKeywords && slideKeywords.length > 0 && backgroundType === 'image') {
      // 키워드를 공백으로 연결하여 검색 쿼리 생성
      const searchQuery = slideKeywords.join(' ');
      
      // Pixabay API 호출
      const searchImages = async () => {
        try {
          const result = await searchPixabayImages(searchQuery, {
            imageType: 'photo',
            orientation: 'horizontal',
            perPage: 6, // 현재 이미지 + 후보 5개 = 6개
            page: 1,
            language: language === 'ko' ? 'ko' : 'en'
          });
          
          if (result.hits && result.hits.length > 0) {
            // 첫 번째 이미지를 현재 배경으로 자동 설정
            const firstImage = result.hits[0];
            const firstImageUrl = getPixabayImageUrl(firstImage, 'large');
            onBackgroundChange?.(firstImageUrl);
            setCurrentPixabayImage(firstImageUrl);
            
            // 나머지 이미지를 후보로 설정 (최대 5개)
            setPixabayCandidates(result.hits.slice(1, 6));
          }
        } catch (error) {
          console.error('Failed to search Pixabay images:', error);
        }
      };
      
      searchImages();
    }
  }, [slideKeywords, backgroundType, language]);

  // 컴포넌트 마운트 시 후보 이미지들 초기화
  useEffect(() => {
    // 초기 후보 이미지들 생성
    const defaultSeed = currentSeed || getRandomSeed();
    const newCandidates = Array.from({ length: 5 }, (_, i) => `${defaultSeed}-alt-${i + 1}`);
    setCandidateSeeds(newCandidates);
    
    // 새로운 후보들에 대한 로딩 상태 초기화
    const initialLoadingState: { [key: string]: boolean } = {};
    newCandidates.forEach(seed => {
      initialLoadingState[seed] = false; // 초기에는 로딩 완료 상태로 설정
    });
    setLoadingImages(initialLoadingState);
    
    // 에러 상태도 초기화
    setErrorImages({});
    setRetryCount({});
  }, []); // 컴포넌트 마운트 시에만 실행

  // currentSeed의 base가 변경될 때만 후보 이미지들 재생성
  useEffect(() => {
    // currentSeed가 비어있으면 기본 시드로 초기화
    if (!currentSeed) {
      const defaultSeed = getRandomSeed();
      const newCandidates = Array.from({ length: 5 }, (_, i) => `${defaultSeed}-alt-${i + 1}`);
      setCandidateSeeds(newCandidates);
      
      // 새로운 후보들에 대한 로딩 상태 초기화
      const initialLoadingState: { [key: string]: boolean } = {};
      newCandidates.forEach(seed => {
        initialLoadingState[seed] = false;
      });
      setLoadingImages(initialLoadingState);
      
      // 에러 상태도 초기화
      setErrorImages({});
      setRetryCount({});
      
      return;
    }
    
    const baseSeed = currentSeed.split('-alt-')[0]; // -alt- 접미사 제거
    
    // 현재 후보들의 baseSeed와 비교
    const currentBaseSeed = candidateSeeds.length > 0 ? candidateSeeds[0].split('-alt-')[0] : '';
    
    // baseSeed가 실제로 변경된 경우에만 후보 재생성
    if (baseSeed !== currentBaseSeed && baseSeed) {
      const newCandidates = Array.from({ length: 5 }, (_, i) => `${baseSeed}-alt-${i + 1}`);
      setCandidateSeeds(newCandidates);
      
      // 새로운 후보들에 대한 로딩 상태 초기화
      const initialLoadingState: { [key: string]: boolean } = {};
      newCandidates.forEach(seed => {
        initialLoadingState[seed] = false;
      });
      setLoadingImages(initialLoadingState);
      
      // 에러 상태도 초기화
      setErrorImages({});
      // 재시도 횟수도 초기화
      setRetryCount({});
      
      // 현재 이미지 로딩 상태도 초기화
      setCurrentImageLoading(true);
      setCurrentImageError(false);
    }
  }, [currentSeed]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      // Clear all retry timers
      Object.values(retryTimersRef.current).forEach(timer => clearTimeout(timer));
      retryTimersRef.current = {};
      
      // Clear current retry timer
      if (currentRetryTimerRef.current) {
        clearTimeout(currentRetryTimerRef.current);
        currentRetryTimerRef.current = null;
      }
    };
  }, []);

  const handleRandomImage = () => {
    const randomSeed = getRandomSeed();
    // 후보 이미지들만 새로 생성하고 현재 이미지는 유지
    const newCandidates = Array.from({ length: 5 }, (_, i) => `${randomSeed}-alt-${i + 1}`);
    setCandidateSeeds(newCandidates);
    
    // 새로운 후보들에 대한 로딩 상태 초기화
    const initialLoadingState: { [key: string]: boolean } = {};
    newCandidates.forEach(seed => {
      initialLoadingState[seed] = false;
    });
    setLoadingImages(initialLoadingState);
    
    // 에러 상태도 초기화
    setErrorImages({});
    setRetryCount({});
  };

  // 파일 업로드 핸들러 (BackgroundChanger 통합 기능)
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onBackgroundChange) {
      // Blob URL 생성하여 즉시 표시
      const blobUrl = URL.createObjectURL(file);
      onBackgroundChange(blobUrl);
      
      // 부모 컴포넌트에 Blob 객체도 전달 (있다면)
      if (onBackgroundBlobChange) {
        onBackgroundBlobChange(file);
      }
    }
  };

  // 현재 배경이 Pixabay 이미지인지 확인
  const [currentPixabayImage, setCurrentPixabayImage] = useState<string | null>(null);
  
  const currentImageUrl = useMemo(() => {
    // 현재 시드가 있으면 해당 이미지 사용 (실제 적용된 배경 표시)
    if (currentSeed && currentSeed !== '') {
      return generatePicsumImage(80, 80, currentSeed, blur, grayscale);
    }
    // 기본 이미지 생성
    const defaultSeed = getRandomSeed();
    return generatePicsumImage(80, 80, defaultSeed, blur, grayscale);
  }, [currentSeed, blur, grayscale]);
  
  const [currentFilteredImage, setCurrentFilteredImage] = useState<string>('');
  const [currentImageProcessing, setCurrentImageProcessing] = useState(false);

  // 이미지 로딩 완료 핸들러
  const handleImageLoad = async (seed: string) => {
    setLoadingImages(prev => ({ ...prev, [seed]: false }));
    setErrorImages(prev => ({ ...prev, [seed]: false }));
    
    // 후보 이미지에 모자이크 필터 적용
    if (currentPattern === 'mosaic' || currentPattern === 'digital-mosaic') {
      const imageUrl = generatePicsumImage(150, 150, seed, blur, grayscale);
      try {
        await applyFilterToImage(imageUrl, seed);
      } catch (error) {
        console.error('Failed to apply filter to candidate image:', error);
      }
    }
  };

  const handleCurrentImageLoad = async () => {
    setCurrentImageLoading(false);
    setCurrentImageError(false);
    
    // 모자이크 필터 적용
    if (currentPattern === 'mosaic' || currentPattern === 'digital-mosaic') {
      try {
        setCurrentImageProcessing(true);
        const filteredUrl = await applyFilterToImage(currentImageUrl, currentSeed);
        setCurrentFilteredImage(filteredUrl);
      } catch (error) {
        console.error('Failed to apply filter to current image:', error);
      } finally {
        setCurrentImageProcessing(false);
      }
    } else {
      setCurrentFilteredImage('');
    }
  };

  // 이미지 에러 핸들러 - 자동 재시도
  const handleImageError = (seed: string) => {
    const currentRetryCount = retryCount[seed] || 0;
    
    if (currentRetryCount < maxRetries) {
      // 재시도 횟수 증가
      setRetryCount(prev => ({ ...prev, [seed]: currentRetryCount + 1 }));
      
      // Clear previous timer if exists
      if (retryTimersRef.current[seed]) {
        clearTimeout(retryTimersRef.current[seed]);
      }
      
      // 1초 후 자동 재시도
      retryTimersRef.current[seed] = setTimeout(() => {
        handleImageReload(seed);
        delete retryTimersRef.current[seed];
      }, 1000);
    } else {
      // 최대 재시도 횟수 초과 시 에러 상태로 설정
      setLoadingImages(prev => ({ ...prev, [seed]: false }));
      setErrorImages(prev => ({ ...prev, [seed]: true }));
    }
  };

  const handleCurrentImageError = () => {
    setCurrentImageLoading(false);
    setCurrentImageError(true);
    
    // Clear previous timer if exists
    if (currentRetryTimerRef.current) {
      clearTimeout(currentRetryTimerRef.current);
    }
    
    // 자동 재시도 (1초 후)
    currentRetryTimerRef.current = setTimeout(() => {
      handleCurrentImageReload();
      currentRetryTimerRef.current = null;
    }, 1000);
  };

  // 이미지 재로드 핸들러
  const handleImageReload = (seed: string) => {
    setLoadingImages(prev => ({ ...prev, [seed]: true }));
    setErrorImages(prev => ({ ...prev, [seed]: false }));
    // 이미지 강제 새로고침을 위해 timestamp 추가
    const img = new Image();
    img.onload = () => handleImageLoad(seed);
    img.onerror = () => handleImageError(seed);
    // Issue #10 fix: Change thumbnail size to 150x150
    img.src = `${generatePicsumImage(150, 150, seed, blur, grayscale)}?t=${Date.now()}`;
  };

  const handleCurrentImageReload = () => {
    setCurrentImageLoading(true);
    setCurrentImageError(false);
    // 메인 이미지 강제 새로고침
    const img = new Image();
    img.onload = () => handleCurrentImageLoad();
    img.onerror = () => handleCurrentImageError();
    img.src = `${currentImageUrl}?t=${Date.now()}`;
  };

  // 이미지에 필터 적용 함수
  const applyFilterToImage = async (imageUrl: string, imageKey: string) => {
    // 모자이크 또는 디지털 모자이크 패턴인 경우에만 필터 적용
    if (currentPattern === 'mosaic' || currentPattern === 'digital-mosaic') {
      const cacheKey = `${imageKey}-${currentPattern}`;
      
      // 이미 처리된 이미지가 있으면 반환
      if (filteredImages[cacheKey]) {
        return filteredImages[cacheKey];
      }
      
      // 현재 처리 중이면 원본 반환
      if (filterProcessing[cacheKey]) {
        return imageUrl;
      }
      
      try {
        setFilterProcessing(prev => ({ ...prev, [cacheKey]: true }));
        
        const mosaicSize = currentPattern === 'mosaic' ? 30 : 30;
        const filteredImageUrl = await applyImageFilter(imageUrl, 'mosaic', { mosaicSize });
        
        setFilteredImages(prev => ({ ...prev, [cacheKey]: filteredImageUrl }));
        setFilterProcessing(prev => ({ ...prev, [cacheKey]: false }));
        
        return filteredImageUrl;
      } catch (error) {
        console.error('Failed to apply filter:', error);
        setFilterProcessing(prev => ({ ...prev, [cacheKey]: false }));
        return imageUrl;
      }
    }
    
    return imageUrl;
  };

  // 패턴이 변경될 때 필터링된 이미지 캐시 초기화 및 현재 이미지 필터 재적용
  useEffect(() => {
    if (currentPattern !== 'mosaic' && currentPattern !== 'digital-mosaic') {
      setFilteredImages({});
      setFilterProcessing({});
      setCurrentFilteredImage('');
    } else {
      // 모자이크 패턴으로 변경된 경우 현재 이미지에 필터 적용
      const applyCurrentFilter = async () => {
        try {
          setCurrentImageProcessing(true);
          const filteredUrl = await applyFilterToImage(currentImageUrl, currentSeed);
          setCurrentFilteredImage(filteredUrl);
        } catch (error) {
          console.error('Failed to apply filter to current image:', error);
        } finally {
          setCurrentImageProcessing(false);
        }
      };
      applyCurrentFilter();
    }
  }, [currentPattern, currentImageUrl, currentSeed]);

  // 그라데이션 배경 옵션들 (중복 제거, 무채색 추가)
  const gradientBackgrounds = [
    // 컬러풀한 그라데이션
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    'linear-gradient(135deg, #fad0c4 0%, #ffd1ff 100%)',
    'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
    'linear-gradient(135deg, #00b894 0%, #00cec9 100%)',
    'linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%)',
    'linear-gradient(135deg, #fd79a8 0%, #fdcb6e 100%)',
    'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
    'linear-gradient(135deg, #55a3ff 0%, #000428 100%)',
    'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)',
    // 무채색 그라데이션
    'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
    'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
    'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
    'linear-gradient(135deg, #fff3e0 0%, #ffcc02 100%)',
    'linear-gradient(135deg, #fce4ec 0%, #f8bbd9 100%)',
    'linear-gradient(135deg, #f1f8e9 0%, #dcedc8 100%)',
    'linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)',
    'linear-gradient(135deg, #fafafa 0%, #e0e0e0 100%)',
    'linear-gradient(135deg, #f5f5f5 0%, #d0d0d0 100%)',
    'linear-gradient(135deg, #eeeeee 0%, #bdbdbd 100%)',
    'linear-gradient(135deg, #e0e0e0 0%, #9e9e9e 100%)',
    'linear-gradient(135deg, #bdbdbd 0%, #757575 100%)',
    'linear-gradient(135deg, #9e9e9e 0%, #616161 100%)',
    'linear-gradient(135deg, #757575 0%, #424242 100%)',
    'linear-gradient(135deg, #616161 0%, #212121 100%)'
  ];

  // 단색 배경 옵션들 (흰색, 검은색 포함 및 확장)
  const solidBackgrounds = [
    '#FFFFFF', '#000000', '#F8F9FA', '#E9ECEF', '#DEE2E6', '#CED4DA',
    '#6C757D', '#495057', '#343A40', '#212529', '#FF6B6B', '#4ECDC4', 
    '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#74B9FF', '#FD79A8', 
    '#FDCB6E', '#A29BFE', '#FF7675', '#00B894', '#00CEC9', '#6C5CE7',
    '#A29BFE', '#FD79A8', '#FDCB6E', '#E17055', '#81ECEC', '#00B894'
  ];

  // 사용자 정의 색상 상태
  const [customColor, setCustomColor] = useState('#3B82F6');

  // 패턴과 필터 옵션들
  const patterns = [
    { name: '패턴/필터', value: 'none' },
    { name: '좁은 격자', value: 'grid-small' },
    { name: '넓은 격자', value: 'grid-large' },
    { name: '매우 넓은 격자', value: 'grid-xlarge' },
    { name: '가로줄', value: 'lines-horizontal' },
    { name: '세로줄', value: 'lines-vertical' },
    { name: '큰 물결', value: 'wave-large' },
    { name: '가느다란 물결', value: 'wave-small' },
    { name: '원형 프랙탈', value: 'circle-fractal' },
    { name: '글래스모픽', value: 'glassmorphic' },
    { name: '모자이크', value: 'mosaic' },
    { name: '디지털 모자이크', value: 'digital-mosaic' },
    { name: '물방울', value: 'water-drops' },
    { name: '무지개', value: 'rainbow' },
    { name: '가우시안 블러', value: 'gaussian-blur' },
    { name: '모션 블러(가로)', value: 'motion-blur-horizontal' },
    { name: '모션 블러(세로)', value: 'motion-blur-vertical' },
    { name: '지그재그', value: 'zigzag' },
    { name: '어안 왜곡', value: 'fisheye' },
    { name: '광각 왜곡', value: 'wide-angle' }
  ];

  // Pixabay 검색 핸들러
  const handlePixabaySearch = async (page = 1, category?: string) => {
    if (!pixabayQuery.trim() && !category) {return;}
    
    setPixabayLoading(true);
    setPixabayError(null);
    setPixabayPage(page);
    
    if (category !== undefined) {
      setPixabayCategory(category);
    }
    
    try {
      const result = await searchPixabayImages(pixabayQuery || category || '', {
        imageType: 'photo',
        orientation: 'horizontal',
        perPage: 12,
        page: page,
        category: category || pixabayCategory,
        language: 'ko'
      });
      
      setPixabayImages(result.hits);
      setPixabayTotalHits(result.totalHits);
    } catch (error) {
      setPixabayError('이미지 검색에 실패했습니다.');
      console.error('Pixabay search error:', error);
    } finally {
      setPixabayLoading(false);
    }
  };

  // Pixabay 이미지 선택 핸들러
  const handlePixabayImageSelect = (image: PixabayImage) => {
    const imageUrl = getPixabayImageUrl(image, 'large');
    
    // 이미지 요소 추가 콜백이 있는 경우 옵션 다이얼로그 표시
    if (onImageElementAdd) {
      setSelectedImageUrl(imageUrl);
      setShowImageOptionDialog(true);
    } else {
      // 콜백이 없는 경우 기존대로 배경 변경
      onBackgroundChange?.(imageUrl);
      setShowPixabaySearch(false);
      setPixabayQuery('');
      setPixabayImages([]);
    }
  };

  // 이미지 사용 옵션 선택 핸들러
  const handleImageOptionSelect = (option: 'background' | 'element') => {
    if (!selectedImageUrl) {return;}

    if (option === 'background') {
      onBackgroundChange?.(selectedImageUrl);
    } else if (option === 'element') {
      onImageElementAdd?.(selectedImageUrl);
    }

    // 다이얼로그와 검색 모달 닫기
    setShowImageOptionDialog(false);
    setSelectedImageUrl(null);
    setShowPixabaySearch(false);
    setPixabayQuery('');
    setPixabayImages([]);
  };

  return (
    <div className="flex gap-0.5 h-full">
      {/* 배경 타입 토글 버튼 - 좌측 고정 */}
      <div className="flex flex-col gap-1 p-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
        <button
          onClick={() => onBackgroundTypeChange && onBackgroundTypeChange('image')}
          className={`px-1.5 py-0.5 text-xs font-medium rounded-md transition-colors ${
            backgroundType === 'image'
              ? 'bg-blue-500 dark:bg-blue-600 text-white'
              : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
          }`}
        >
          이미지
        </button>
        <button
          onClick={() => onBackgroundTypeChange && onBackgroundTypeChange('color')}
          className={`px-1.5 py-0.5 text-xs font-medium rounded-md transition-colors ${
            backgroundType === 'color'
              ? 'bg-blue-500 dark:bg-blue-600 text-white'
              : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
          }`}
        >
          색상
        </button>
        <button
          onClick={() => onBackgroundTypeChange && onBackgroundTypeChange('animated')}
          className={`px-1.5 py-0.5 text-xs font-medium rounded-md transition-colors ${
            backgroundType === 'animated'
              ? 'bg-blue-500 dark:bg-blue-600 text-white'
              : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
          }`}
        >
          애니
        </button>
      </div>

      {/* 선택된 타입에 따른 컨텐츠 영역 */}
      <div className="flex-1 flex items-start gap-2">
        {/* 이미지 관련 컨트롤들 */}
        {backgroundType === 'image' && (
        <div className="flex flex-wrap gap-0">

          {/* 이미지 미리보기 - 현재/이전 슬라이드 비교 */}
          <div className="flex items-start gap-1">
              {/* 첫 번째 슬라이드 배경 - 클릭 가능 */}
              <div className="relative w-[54px] h-[54px] sm:w-[70px] sm:h-[70px] rounded overflow-hidden border hover:border-blue-300 transition-colors" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {firstSlideBackground ? (
                  <button
                    onClick={() => {
                      // 첫 번째 슬라이드와 동일한 배경 적용
                      if (firstSlideBackground.type === 'image' && firstSlideBackground.seed) {
                        onSeedChange(firstSlideBackground.seed);
                        const imageUrl = generatePicsumImage(width, height, firstSlideBackground.seed, blur, grayscale);
                        onBackgroundChange?.(imageUrl);
                      } else if (firstSlideBackground.type === 'color' && firstSlideBackground.color) {
                        onBackgroundTypeChange?.('color');
                        onBackgroundChange?.(firstSlideBackground.color);
                      } else if (firstSlideBackground.type === 'animated' && firstSlideBackground.animatedEffect) {
                        onBackgroundTypeChange?.('animated');
                        onAnimatedEffectChange?.(firstSlideBackground.animatedEffect);
                        if (firstSlideBackground.animatedOptions) {
                          onAnimatedOptionsChange?.(firstSlideBackground.animatedOptions);
                        }
                      }
                    }}
                    className="w-full h-full relative hover:border-blue-300 transition-colors"
                    title="첫 번째 슬라이드 배경 적용"
                  >
                    {firstSlideBackground.type === 'image' && firstSlideBackground.seed ? (
                                          <img
                      src={generatePicsumImage(60, 60, firstSlideBackground.seed, blur, grayscale)}
                      alt="First slide background"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', top: 0, left: 0 }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = generatePicsumImage(60, 60, 'default', blur, grayscale);
                      }}
                    />
                    ) : firstSlideBackground.type === 'color' && firstSlideBackground.color ? (
                      <div 
                        className="w-full h-full"
                        style={{ backgroundColor: firstSlideBackground.color }}
                      />
                    ) : firstSlideBackground.type === 'animated' ? (
                      <div className="w-full h-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center">
                        <span className="text-white text-xs">✨</span>
                      </div>
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-500 text-xs">기준</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black bg-opacity-20 hover:bg-opacity-10 flex items-center justify-center transition-opacity">
                      <span className="text-white text-xs font-medium text-center">기준</span>
                    </div>
                  </button>
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-500 text-xs">기준</span>
                  </div>
                )}
              </div>

              {/* 현재 이미지 미리보기 */}
              <div className="relative w-[54px] h-[54px] sm:w-[70px] sm:h-[70px] rounded overflow-hidden border-2 border-blue-500" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {(currentImageLoading || currentImageError) && (
                  <div className="absolute inset-0 bg-gray-200 rounded flex flex-col items-center justify-center z-10">
                    <Loader className="w-4 h-4 animate-spin text-gray-400" />
                    {currentImageError && (
                      <span className="text-xs text-gray-500 mt-1">재시도 중...</span>
                    )}
                  </div>
                )}
                {!currentImageError && (
                  <>
                    <img
                      src={currentFilteredImage || currentImageUrl}
                      alt="Current background"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', top: 0, left: 0 }}
                      onLoad={() => handleCurrentImageLoad()}
                      onError={() => handleCurrentImageError()}
                    />
                    {currentImageProcessing && (
                      <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                        <Loader className="w-2 h-2 animate-spin text-white" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <div className="absolute inset-0 bg-black bg-opacity-20 hover:bg-opacity-10 flex items-center justify-center transition-opacity">
                      <span className="text-white text-xs font-medium text-center">현재</span>
                    </div>
                  </>
                )}
              </div>
              
              {/* 후보 이미지들 및 버튼들 */}
              <div className="flex flex-col gap-0">
                {/* 버튼들 */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className={`p-1 rounded transition-colors ${
                      isDarkMode 
                        ? 'hover:bg-gray-700 text-gray-300' 
                        : 'hover:bg-gray-100 text-gray-600'
                    }`}
                    title={TRANSLATIONS[language].uploadImage}
                  >
                    <Upload className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShowPixabaySearch(true)}
                    className={`p-1 rounded transition-colors ${
                      isDarkMode 
                        ? 'hover:bg-gray-700 text-gray-300' 
                        : 'hover:bg-gray-100 text-gray-600'
                    }`}
                    title="이미지 검색"
                  >
                    <Search className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleRandomImage}
                    className={`p-1 rounded transition-colors ${
                      isDarkMode 
                        ? 'hover:bg-gray-700 text-gray-300' 
                        : 'hover:bg-gray-100 text-gray-600'
                    }`}
                    title="랜덤 이미지"
                  >
                    <Shuffle className="w-4 h-4" />
                  </button>
                </div>
                
                {/* 후보 이미지 그리드 */}
                <div className="grid grid-cols-5 gap-1">
              {/* Pixabay 이미지가 있으면 Pixabay 이미지를 표시, 없으면 기존 랜덤 이미지 표시 */}
              {pixabayCandidates.length > 0 ? (
                // Pixabay 이미지 표시
                pixabayCandidates.map((image, index) => (
                  <button
                    key={image.id}
                    onClick={() => {
                      const imageUrl = getPixabayImageUrl(image, 'large');
                      onBackgroundChange?.(imageUrl);
                      setCurrentPixabayImage(imageUrl);
                    }}
                    className="relative aspect-square rounded overflow-hidden border hover:border-blue-300 transition-colors"
                    title={`${language === 'ko' ? '관련 이미지' : 'Related image'} ${index + 1}`}
                  >
                    <img
                      src={image.previewURL}
                      alt={image.tags}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-10 hover:bg-opacity-0 transition-opacity" />
                  </button>
                ))
              ) : (
                // 기존 랜덤 이미지 표시
                candidateSeeds.map((candidateSeed, index) => {
                  // 현재 이미지와 동일한 크기로 설정
                  const candidateUrl = generatePicsumImage(30, 30, candidateSeed, blur, grayscale);
                  const isLoading = loadingImages[candidateSeed];
                  const hasError = errorImages[candidateSeed];
                  
                  return (
                    <button
                      key={candidateSeed}
                      onClick={() => {
                        if (!isLoading && !hasError) {
                          onSeedChange(candidateSeed);
                          // 배경 이미지도 함께 적용
                          const imageUrl = generatePicsumImage(width, height, candidateSeed, blur, grayscale);
                          onBackgroundChange?.(imageUrl);
                          setCurrentPixabayImage(null); // 랜덤 이미지 선택 시 Pixabay 이미지 해제
                        }
                      }}
                      className="relative w-[30px] h-[30px] sm:w-[40px] sm:h-[40px] rounded overflow-hidden border hover:border-blue-300 transition-colors"
                      title={isLoading ? `이미지 로딩 중...` : hasError ? `재시도 중... (${retryCount[candidateSeed] || 0}/${maxRetries})` : `후보 이미지 ${index + 1}`}
                      disabled={isLoading || hasError}
                    >
                      {(isLoading || hasError) && (
                        <div className="absolute inset-0 bg-gray-200 rounded flex flex-col items-center justify-center z-10">
                          <Loader className="w-4 h-4 animate-spin text-gray-400" />
                          {hasError && (
                            <span className="text-xs text-gray-500 mt-1">
                              재시도 {retryCount[candidateSeed] || 0}/{maxRetries}
                            </span>
                          )}
                        </div>
                      )}
                      {!hasError && (
                        <>
                          <img
                            src={candidateUrl} 
                            alt={`Candidate ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              // 에러 시 기본 이미지로 대체
                              target.src = generatePicsumImage(30, 30, 'default', blur, grayscale);
                            }}
                          />
                          {(() => {
                            const cacheKey = `${candidateSeed}-${currentPattern}`;
                            return filterProcessing[cacheKey] && (
                              <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                                <Loader className="w-2 h-2 animate-spin text-white" />
                              </div>
                            );
                          })()}
                          <div className="absolute inset-0 bg-black bg-opacity-10 hover:bg-opacity-0 transition-opacity" />
                        </>
                      )}
                    </button>
                  );
                })
              )}
                </div>
              </div>
            </div>

          {/* 이미지 효과 컨트롤 - 모바일 1행 배치 */}
          <div className="flex flex-row flex-wrap gap-2 w-full items-center">
            {/* 블러 조절 */}
            <div className="flex items-center gap-1">
              <span className={`text-[10px] font-medium ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                블러
              </span>
              <input
                type="range"
                min="0"
                max="3"
                value={blur}
                onChange={(e) => onBlurChange(parseInt(e.target.value))}
                className={`h-1.5 rounded-lg appearance-none cursor-pointer w-[60px] sm:w-[80px] ${
                  isDarkMode ? 'bg-gray-600' : 'bg-gray-200'
                }`}
              />
              <span className={`text-[10px] min-w-[15px] text-center ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {blur}
              </span>
            </div>

            {/* 그레이스케일 토글 */}
            <div className="flex items-center gap-1">
              <span className={`text-[10px] font-medium ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                흑백
              </span>
              <button
                onClick={() => onGrayscaleChange(!grayscale)}
                className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors ${
                  grayscale ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform ${
                    grayscale ? 'translate-x-[18px]' : 'translate-x-[2px]'
                  }`}
                />
              </button>
            </div>

            {/* 패턴/필터 선택 */}
            <div className="flex items-center gap-1">
              <select
                value={currentPattern}
                onChange={(e) => onPatternChange && onPatternChange(e.target.value)}
                className={`px-1.5 py-1 border rounded text-[10px] w-[90px] sm:w-[120px] ${
                  isDarkMode 
                    ? 'bg-gray-600 border-gray-500 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                {patterns.map((pattern) => (
                  <option key={pattern.value} value={pattern.value}>
                    {pattern.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Issue #11 fix: Hide seed display */}
        </div>
      )}

        {/* 컬러 배경 옵션 */}
        {backgroundType === 'color' && (
          <div className="flex flex-wrap gap-2 h-full">
            {/* 그라데이션 옵션 */}
            <div className="flex-1 min-w-0">
              <label className={`text-xs font-medium ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                그라데이션
              </label>
              <div className="flex flex-wrap gap-0.5">
                {gradientBackgrounds.map((gradient, index) => (
                  <button
                    key={`gradient-${index}`}
                    onClick={() => onBackgroundChange && onBackgroundChange(gradient)}
                    className="w-3.5 h-3.5 sm:w-5 sm:h-5 rounded border hover:border-blue-300 transition-colors"
                    style={{ background: gradient }}
                    title={`그라데이션 ${index + 1}`}
                  />
                ))}
              </div>
            </div>
            
            {/* 단색 옵션 */}
            <div className="flex-1 min-w-0">
              <label className={`text-xs font-medium ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                단색
              </label>
              <div className="flex flex-wrap gap-0.5">
                {solidBackgrounds.slice(0, 27).map((color, index) => (
                  <button
                    key={`solid-${index}`}
                    onClick={() => onBackgroundChange && onBackgroundChange(color)}
                    className={`w-3.5 h-3.5 sm:w-5 sm:h-5 rounded border hover:border-blue-300 transition-colors ${
                      color === '#FFFFFF' ? 'border-gray-300' : ''
                    }`}
                    style={{ backgroundColor: color }}
                    title={
                      color === '#FFFFFF' ? '흰색' : 
                      color === '#000000' ? '검은색' : 
                      `단색 ${index + 1}`
                    }
                  />
                ))}
              </div>
            </div>

            {/* 사용자 정의 색상 */}
            <div className="flex flex-col items-center gap-1">
              <label className={`text-xs font-medium ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                픽
              </label>
              <input
                type="color"
                value={customColor}
                onChange={(e) => {
                  setCustomColor(e.target.value);
                  // 색상 변경 시 즉시 적용
                  onBackgroundChange?.(e.target.value);
                }}
                className="w-5 h-5 sm:w-6 sm:h-6 rounded border cursor-pointer"
                title="색상 선택"
              />
            </div>
          </div>
        )}

        {/* 애니메이션 배경 관련 컨트롤들 */}
        {backgroundType === 'animated' && (
          <div className="flex items-start gap-2 h-full">
          {/* 효과 선택 */}
          <select
            value={animatedEffect}
            onChange={(e) => onAnimatedEffectChange && onAnimatedEffectChange(e.target.value)}
            className={`px-2 py-1 border rounded text-xs ${
              isDarkMode 
                ? 'bg-gray-600 border-gray-500 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value="fog">안개</option>
            <option value="globe">지구</option>
            <option value="net">네트워크</option>
            <option value="cells">세포</option>
            <option value="rings">링</option>
            <option value="halo">헤일로</option>
          </select>

          {/* 커스터마이즈 옵션들 */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* 속도 옵션 - 모든 애니메이션에 적용 */}
            <div className="flex items-center gap-1">
              <label className={`text-xs whitespace-nowrap ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>속도:</label>
              <input
                type="range"
                min="0.5"
                max="3"
                step="0.1"
                value={animatedOptions.speed || 1}
                onChange={(e) => onAnimatedOptionsChange && onAnimatedOptionsChange({
                  ...animatedOptions,
                  speed: parseFloat(e.target.value)
                })}
                className="w-16"
              />
              <span className={`text-xs min-w-[2rem] ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>{(animatedOptions.speed || 1).toFixed(1)}</span>
            </div>
            
            {/* 크기 옵션 - 특정 애니메이션에만 적용 */}
            {['waves', 'clouds', 'clouds2', 'globe', 'net', 'cells'].includes(animatedEffect) && (
              <div className="flex items-center gap-1">
                <label className={`text-xs whitespace-nowrap ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>크기:</label>
                <input
                  type="range"
                  min="0.5"
                  max="2"
                  step="0.1"
                  value={animatedOptions.scale || 1}
                  onChange={(e) => onAnimatedOptionsChange && onAnimatedOptionsChange({
                    ...animatedOptions,
                    scale: parseFloat(e.target.value)
                  })}
                  className="w-16"
                />
                <span className={`text-xs min-w-[2rem] ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>{(animatedOptions.scale || 1).toFixed(1)}</span>
              </div>
            )}

            {/* 색상 옵션 - 효과에 따라 다르게 표시 */}
            {(animatedEffect === 'topology' || animatedEffect === 'trunk') ? (
              // topology와 trunk는 단일 색상만 사용
              <div className="flex items-center gap-1">
                <label className={`text-xs whitespace-nowrap ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>색상:</label>
                <input
                  type="color"
                  value={animatedOptions.color1 || '#ff3f81'}
                  onChange={(e) => onAnimatedOptionsChange && onAnimatedOptionsChange({
                    ...animatedOptions,
                    color1: e.target.value
                  })}
                  className="w-8 h-6 rounded cursor-pointer"
                />
              </div>
            ) : (['birds', 'dots', 'net', 'cells', 'globe'].includes(animatedEffect) && (
              // 이중 색상을 사용하는 애니메이션
              <div className="flex items-center gap-1">
                <label className={`text-xs whitespace-nowrap ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>색상:</label>
                <input
                  type="color"
                  value={animatedOptions.color1 || '#ff3f81'}
                  onChange={(e) => onAnimatedOptionsChange && onAnimatedOptionsChange({
                    ...animatedOptions,
                    color1: e.target.value
                  })}
                  className="w-6 h-6 rounded cursor-pointer"
                />
                <input
                  type="color"
                  value={animatedOptions.color2 || '#ff6e40'}
                  onChange={(e) => onAnimatedOptionsChange && onAnimatedOptionsChange({
                    ...animatedOptions,
                    color2: e.target.value
                  })}
                  className="w-6 h-6 rounded cursor-pointer"
                />
              </div>
            ))}
            
            {/* 배경색 옵션 */}
            <div className="flex items-center gap-1">
              <label className={`text-xs whitespace-nowrap ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>배경:</label>
              <input
                type="color"
                value={animatedOptions.backgroundColor || '#000000'}
                onChange={(e) => onAnimatedOptionsChange && onAnimatedOptionsChange({
                  ...animatedOptions,
                  backgroundColor: e.target.value
                })}
                className="w-8 h-6 rounded cursor-pointer"
              />
            </div>
          </div>
          </div>
        )}

      </div>
      
      {/* Pixabay 검색 모달 */}
      {showPixabaySearch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className={`w-full max-w-4xl mx-4 max-h-[80vh] rounded-lg shadow-xl flex flex-col ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            {/* 모달 헤더 */}
            <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  이미지 검색
                </h3>
                <button
                  onClick={() => {
                    setShowPixabaySearch(false);
                    setPixabayQuery('');
                    setPixabayImages([]);
                    setPixabayCategory('');
                    setPixabayPage(1);
                    setPixabayError(null);
                  }}
                  className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
                >
                  <span className="text-xl">×</span>
                </button>
              </div>
              
              {/* 검색 입력 */}
              <div className="mt-4 flex gap-2">
                <input
                  type="text"
                  value={pixabayQuery}
                  onChange={(e) => setPixabayQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handlePixabaySearch(1)}
                  placeholder="검색어를 입력하세요..."
                  className={`flex-1 px-4 py-2 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                />
                <button
                  onClick={() => handlePixabaySearch(1)}
                  disabled={pixabayLoading}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    pixabayLoading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
                >
                  {pixabayLoading ? (
                    <Loader className="w-5 h-5 animate-spin" />
                  ) : (
                    '검색'
                  )}
                </button>
              </div>
              
              {/* 카테고리 버튼들 */}
              <div className="mt-3 flex flex-wrap gap-2">
                {[
                  { name: '전체', value: '' },
                  { name: '자연', value: 'nature' },
                  { name: '비즈니스', value: 'business' },
                  { name: '기술', value: 'science' },
                  { name: '교육', value: 'education' },
                  { name: '건강', value: 'health' },
                  { name: '여행', value: 'travel' },
                  { name: '음식', value: 'food' },
                  { name: '건축', value: 'buildings' },
                  { name: '스포츠', value: 'sports' },
                  { name: '패션', value: 'fashion' },
                  { name: '예술', value: 'art' },
                  { name: '음악', value: 'music' }
                ].map((cat) => (
                  <button
                    key={cat.value}
                    onClick={() => {
                      setPixabayQuery('');
                      handlePixabaySearch(1, cat.value);
                    }}
                    className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                      pixabayCategory === cat.value
                        ? 'bg-blue-500 text-white'
                        : isDarkMode
                          ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
            
            {/* 검색 결과 */}
            <div className="flex-1 overflow-y-auto p-4">
              {pixabayError && (
                <div className="text-center py-8 text-red-500">
                  {pixabayError}
                </div>
              )}
              
              {pixabayImages.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {pixabayImages.map((image) => (
                    <button
                      key={image.id}
                      onClick={() => handlePixabayImageSelect(image)}
                      className="relative group overflow-hidden rounded-lg border-2 border-transparent hover:border-blue-500 transition-all"
                    >
                      <img
                        src={image.webformatURL}
                        alt={image.tags}
                        className="w-full h-16 object-cover"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity flex items-center justify-center">
                        <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity">
                          선택
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              ) : !pixabayLoading && pixabayQuery && (
                <div className="text-center py-8 text-gray-500">
                  검색 결과가 없습니다.
                </div>
              )}
            </div>
            
            {/* 페이지네이션 */}
            {pixabayImages.length > 0 && pixabayTotalHits > 12 && (
              <div className={`p-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => handlePixabaySearch(pixabayPage - 1)}
                    disabled={pixabayPage === 1 || pixabayLoading}
                    className={`px-3 py-1 rounded text-sm ${
                      pixabayPage === 1 || pixabayLoading
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300'
                    }`}
                  >
                    이전
                  </button>
                  
                  <span className={`px-3 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {pixabayPage} / {Math.ceil(pixabayTotalHits / 12)}
                  </span>
                  
                  <button
                    onClick={() => handlePixabaySearch(pixabayPage + 1)}
                    disabled={pixabayPage >= Math.ceil(pixabayTotalHits / 12) || pixabayLoading}
                    className={`px-3 py-1 rounded text-sm ${
                      pixabayPage >= Math.ceil(pixabayTotalHits / 12) || pixabayLoading
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300'
                    }`}
                  >
                    다음
                  </button>
                </div>
              </div>
            )}
            
            {/* 모달 푸터 */}
            <div className={`p-4 border-t text-xs text-center ${
              isDarkMode 
                ? 'border-gray-700 text-gray-400' 
                : 'border-gray-200 text-gray-500'
            }`}>
              Powered by Pixabay
            </div>
          </div>
        </div>
      )}

      {/* 이미지 사용 옵션 다이얼로그 */}
      {showImageOptionDialog && selectedImageUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className={`w-full max-w-md mx-4 rounded-lg shadow-xl ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                이미지 사용 방법 선택
              </h3>
            </div>
            
            <div className="p-4">
              <div className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                선택한 이미지를 어떻게 사용하시겠습니까?
              </div>
              
              {/* 이미지 미리보기 */}
              <div className="mb-4">
                <img
                  src={selectedImageUrl}
                  alt="Selected"
                  className="w-full h-16 object-cover rounded-lg"
                />
              </div>
              
              <div className="space-y-3">
                <button
                  onClick={() => handleImageOptionSelect('background')}
                  className={`w-full p-3 text-left rounded-lg border transition-colors ${
                    isDarkMode 
                      ? 'border-gray-600 hover:border-blue-500 hover:bg-gray-700' 
                      : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                  }`}
                >
                  <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    🖼️ 배경으로 설정
                  </div>
                  <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    슬라이드의 배경 이미지로 사용합니다
                  </div>
                </button>
                
                <button
                  onClick={() => handleImageOptionSelect('element')}
                  className={`w-full p-3 text-left rounded-lg border transition-colors ${
                    isDarkMode 
                      ? 'border-gray-600 hover:border-blue-500 hover:bg-gray-700' 
                      : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                  }`}
                >
                  <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    🎯 요소로 추가
                  </div>
                  <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    편집 가능한 이미지 요소로 슬라이드에 추가합니다
                  </div>
                </button>
              </div>
            </div>
            
            <div className={`p-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => {
                  setShowImageOptionDialog(false);
                  setSelectedImageUrl(null);
                }}
                className={`w-full px-4 py-2 rounded-lg transition-colors ${
                  isDarkMode 
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }`}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 파일 업로드 입력 (숨김) */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        className="hidden"
      />
    </div>
  );
};